import type {
  ChatThreadView,
  CopyWeekResult,
  LoginLinkListView,
  DayView,
  LessonDetail,
  LessonUpdateInput,
  MeView,
  TemplatePayload,
  TaskListView,
  TaskState,
  Template,
  WeekView,
} from '@skolveckan/shared';

/**
 * Alla anrop går till /api relativt. I drift serverar servern klienten, så det
 * finns ingen bas-URL att konfigurera — och inget CORS att glömma.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? 'internal',
      body?.message ?? 'Något gick fel.',
      body?.details,
    );
  }
  return body as T;
}

export const api = {
  me: () => request<MeView>('/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  day: (date?: string, classId?: string) => {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (classId) q.set('classId', classId);
    return request<DayView>(`/schedule/day?${q}`);
  },

  week: (week?: string, classId?: string) => {
    const q = new URLSearchParams();
    if (week) q.set('week', week);
    if (classId) q.set('classId', classId);
    return request<WeekView>(`/schedule/week?${q}`);
  },

  coverage: (week?: string, classId?: string) => {
    const q = new URLSearchParams();
    if (week) q.set('week', week);
    if (classId) q.set('classId', classId);
    return request<{ week: string; total: number; filled: number }>(`/schedule/coverage?${q}`);
  },

  lesson: (id: string) => request<LessonDetail>(`/lessons/${id}`),

  saveLesson: (id: string, input: LessonUpdateInput) =>
    request<LessonDetail>(`/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  applyTemplate: (lessonId: string, templateId: string, opts?: { overwrite?: boolean; commit?: boolean }) =>
    request<{ preview: LessonDetail; committed: boolean }>(
      `/lessons/${lessonId}/apply-template`,
      {
        method: 'POST',
        body: JSON.stringify({ templateId, ...opts }),
      },
    ),

  tasks: (scope: 'today' | 'week' | 'all' = 'week') =>
    request<TaskListView>(`/tasks?scope=${scope}`),

  setTaskState: (id: string, state: TaskState) =>
    request<{ id: string; state: TaskState }>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ state }),
    }),

  createTask: (input: { title: string; dueDate: string | null; dueMin: number | null }) =>
    request<{ id: string }>('/tasks', { method: 'POST', body: JSON.stringify(input) }),

  deleteTask: (id: string) => request<void>(`/tasks/${id}`, { method: 'DELETE' }),

  copyWeek: (fromWeek: string, toWeek: string, overwrite = false, classId?: string) =>
    request<CopyWeekResult>('/schedule/copy-week', {
      method: 'POST',
      body: JSON.stringify({ fromWeek, toWeek, overwrite, classId: classId ?? null }),
    }),

  templates: (classId?: string) => {
    const q = classId ? `?classId=${classId}` : '';
    return request<{ templates: Template[] }>(`/templates${q}`);
  },

  /**
   * Mallen byggs av formulärets nuvarande innehåll, inte av den sparade
   * lektionen. En lärare som just skrivit klart och trycker "Spara som mall"
   * menar det som står på skärmen.
   */
  createTemplate: (input: {
    name: string;
    description: string;
    subjectId: string | null;
    payload: TemplatePayload;
  }) => request<Template>('/templates', { method: 'POST', body: JSON.stringify(input) }),

  loginLinks: (classId?: string) => {
    const q = classId ? `?classId=${classId}` : '';
    return request<LoginLinkListView>(`/login-links${q}`);
  },

  createLoginLink: (studentUserId: string, classId?: string) =>
    request<{ studentUserId: string; url: string }>('/login-links', {
      method: 'POST',
      body: JSON.stringify({ studentUserId, classId: classId ?? null }),
    }),

  revokeLoginLink: (studentUserId: string, classId?: string) => {
    const q = classId ? `?classId=${classId}` : '';
    return request<void>(`/login-links/${studentUserId}${q}`, { method: 'DELETE' });
  },

  chat: (lessonId: string) => request<ChatThreadView>(`/lessons/${lessonId}/chat`),

};

/**
 * Chatten strömmas över samma endpoint som den icke-strömmande varianten.
 *
 * EventSource kan bara göra GET, och frågan ska in i en body — därför fetch
 * med en egen läsare i stället. Ramarna är vanlig SSE, så servern behöver
 * inte veta vilken av klienterna som frågar.
 */
export async function streamChat(
  lessonId: string,
  message: string,
  on: {
    onStudent?: (msg: { id: string; content: string; createdAt: number }) => void;
    onDelta: (text: string) => void;
    onDone: (msg: { id: string; content: string; createdAt: number }) => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/lessons/${lessonId}/chat`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({ message }),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    const body = text ? (JSON.parse(text) as { error?: string; message?: string }) : null;
    throw new ApiError(res.status, body?.error ?? 'internal', body?.message ?? 'Något gick fel.');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // En ram slutar på en tom rad. Allt före sista tomraden är färdiga ramar;
    // resten är en halv ram som får vänta på nästa chunk.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) continue;

      const payload = JSON.parse(data);
      if (event === 'student') on.onStudent?.(payload);
      else if (event === 'delta') on.onDelta(payload.text);
      else if (event === 'done') on.onDone(payload);
    }
  }
}
